/**
 * layout-shape-adapter — F12.N pre-E2 fix.
 *
 * Builder `SystemModuleBuilderPage.tsx` operuje wewnętrznie na STARYM
 * płaskim modelu `LayoutTab[]` (z F12.M), ale po E1.T7 backend (walidator +
 * skrypt migracji) wymaga NOWEGO rekurencyjnego kształtu (`elements[0].tabs[]`,
 * ADR-0028).
 *
 * Adapter w obie strony:
 *  - `loadTabsFromConfig()` — odczyt: nowy kształt → stary `LayoutTab[]` w state.
 *  - `saveTabsToConfig()`   — zapis: stary `LayoutTab[]` → nowy kształt w body PUT.
 *
 * To rozwiązanie TYMCZASOWE — pełen refactor buildera na natywny rekurencyjny
 * model przyjdzie w Etapach 2-4 F12.N (DnD, paleta klocków, akordeon/stepper).
 * Wtedy adapter można usunąć.
 *
 * Symetria z `migrate-layout-to-recursive.ts` — tamten jednorazowy skrypt
 * migruje stary→nowy raz w bazie. Tu mamy konwersję w runtime w obie strony.
 *
 * Obsługa fallback:
 *  - Nowy kształt z `elements[0].tabs[]` → unpack.
 *  - Stary kształt z `tabs[]` (pre-F12.N) → passthrough.
 *  - Pre-C.7 kształt z `sections[]` na root → opakuj w jedyną zakładkę 'Dane'.
 *  - Pusty / niepoprawny → `[]`.
 *
 * „Pola dodatkowe" NIE są blokiem ani zakładką: adapter nie generuje już elementu
 * `ext_fields_view`, a przy odczycie usuwa go z zapisanego układu — z dzieci zakładki i z wnętrza
 * zagnieżdżonych sekcji (zakładkę opróżnioną przez usunięcie bloku z pierwszego poziomu usuwa
 * razem z nim, jak backfill po stronie API; opróżniona sekcja zostaje). Zakładka starego kształtu `type: 'ext_fields'`
 * jest pomijana (nie ma jej w `TabType`).
 */

import {
  type EmbeddedTableColumn,
  parseEmbeddedTableColumns,
  parseEmbeddedTableHiddenColumnIds,
} from '@saas/shared';

// ─── Typy (zduplikowane z SystemModuleBuilderPage.tsx żeby uniknąć cyclic imports) ──

type TabType =
  | 'fields'
  | 'subcollection'
  | 'audit_log'
  | 'tree_picker'
  | 'satellite'
  /** New: real additional table tab (has tableSlug). */
  | 'additional'
  /**
   * F22 (widoki dodatkowe) — sekcja read-only zasilana zapytaniem SQL z katalogu
   * `ext_additional_views` (ma `viewSlug`, PHASE_22_PLAN.md §8). Odrębny od
   * 'additional' (tabele dodatkowe = edytowalne siatki wierszy z własną tabelą
   * danych) — widok nie ma tabeli wierszy, tylko `source_sql`.
   */
  | 'additional_query'
  /** New: neutral placeholder tab (security/account — no tableSlug). */
  | 'panel'
  /**
   * Etap 2 kartoteki — zakładka Akceptacje (F17 T17.1.8). Seed ją tworzy;
   * builder wyświetla read-only placeholder. Adapter: save → pusty children.
   */
  | 'approvals'
  /**
   * Zakładka-kontener bloków (konfigurowalne zakładki, Faza 1). Zawiera wyłącznie
   * bloki-widoki (np. `contractor_contacts_view`, `attachments_view`, `process_view`)
   * — bez sekcji z polami. Builder renderuje ją przez wspólną ścieżkę non-fields
   * (paleta klocków + `NonFieldsExtrasPanel`), więc bloki są widoczne, usuwalne i
   * dokładalne. Round-trip: children = same bloki (`extras`), bez owijania.
   */
  | 'block';

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  settings?: Record<string, unknown>;
  style?: Record<string, unknown>;
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
  children?: LayoutElement[];
}

interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: LayoutElement[];
}

interface LayoutTab {
  id: string;
  slug?: string;
  title: string;
  /** F12.N Etap 4 E4.T4: nazwa ikony z lucide-react (np. `'FileText'`). */
  icon?: string;
  type: TabType;
  hidden: boolean;
  sortOrder: number;
  visibleRoleIds?: string[];
  /** ETAP4 S7: ISO timestamp publikacji zakładki (wysłana do klientów = trwała). */
  publishedAt?: string;
  sections?: LayoutSection[];
  collection?: string;
  /** F12S Etap C T12.S.C.4: slug tabeli dodatkowej powiązanej z tą zakładką. */
  tableSlug?: string;
  /**
   * Układ kolumn tabeli dodatkowej (`type='additional'`) albo widoku SQL (`type='additional_query'`)
   * tej zakładki — w zapisie siedzi w `settings.tableColumns` placeholdera `additional_view` /
   * `additional_query_view`. Brak = domyślny układ (klucz NIE jest wtedy zapisywany).
   */
  tableColumns?: EmbeddedTableColumn[];
  /**
   * Maska ukrytych kolumn tabeli dodatkowej (`type='additional'`) albo widoku SQL
   * (`type='additional_query'`) tej zakładki — w zapisie `settings.hiddenColumnIds` placeholdera
   * `additional_view` / `additional_query_view`. Na układzie systemowym niesie ukrycia tenanta
   * (nakładka scalona na odczycie). Pusta / brak = klucz NIE jest zapisywany.
   */
  hiddenColumnIds?: string[];
  /** F22: slug widoku dodatkowego (`ext_additional_views.slug`) powiązanego z tą zakładką. */
  viewSlug?: string;
  /**
   * F12.N E2.T1: extras — dodatkowe klocki layoutu (heading/alert/separator/...)
   * dodane przez admina do zakładki POZA główną zawartością (sekcje dla 'fields',
   * placeholder typu related_records_table/audit_log_view dla pozostałych).
   *
   * W nowym kształcie zapisywane są w `TabItem.children` PO głównym contencie:
   *  - fields: [section, section, ..., ...extras]
   *  - subcollection: [related_records_table, (subcollection_form_layout?), ...extras]
   *  - audit_log: [audit_log_view, ...extras]
   *  - itd.
   */
  extras?: LayoutElement[];
  /**
   * F12 PR 2 pkt 2 v3 (2026-05-19): customowy form layout dla `type='subcollection'`.
   * Sekcje + pola modułu wskazywanego. W adapterze SAVE: dorzucamy element typu
   * `subcollection_form_layout` po `related_records_table` w `TabItem.children`.
   * W LOAD: ekstrahujemy sekcje z elementu type='subcollection_form_layout'.
   */
  subcollectionFormLayout?: LayoutSection[];
}

// ─── Nowy kształt — minimalne shape'y (post-F12.N) ──

interface NewTabItem {
  id: string;
  slug: string;
  title: string;
  /** F12.N Etap 4 E4.T4: nazwa ikony z lucide-react. */
  icon?: string;
  hidden?: boolean;
  sortOrder?: number;
  visibleRoleIds?: string[];
  /** ETAP4 S7: znacznik publikacji zakładki (round-trip przez JSONB układu). */
  publishedAt?: string;
  children: LayoutElement[];
}

interface NewTabsElement {
  id: string;
  type: 'tabs';
  tabs: NewTabItem[];
}

interface NewLayoutConfig {
  /**
   * T2: obok elementu `tabs` mogą stać elementy VIEW-LEVEL (page-header/highlight)
   * jako rodzeństwo PRZED nim. Runtime mapuje `elements` po kolejności, więc
   * renderują się nad zakładkami. Typ poluzowany do unii, żeby round-trip zachował
   * rodzeństwo (wcześniej zawsze `[tabsElement]`).
   */
  elements: (NewTabsElement | LayoutElement)[];
}

// ─── Detektory ──

function isNewShape(cfg: unknown): cfg is { elements: NewTabsElement[] } {
  if (!cfg || typeof cfg !== 'object') return false;
  const c = cfg as { elements?: unknown };
  return Array.isArray(c.elements) && c.elements.length > 0;
}

function isOldShape(cfg: unknown): cfg is { tabs: LayoutTab[] } {
  if (!cfg || typeof cfg !== 'object') return false;
  const c = cfg as { tabs?: unknown };
  return Array.isArray(c.tabs);
}

function isPreC7Shape(cfg: unknown): cfg is { sections: LayoutSection[] } {
  if (!cfg || typeof cfg !== 'object') return false;
  const c = cfg as { sections?: unknown };
  return Array.isArray(c.sections);
}

// ─── LOAD: nowy → stary ──

/**
 * Czyta `layoutConfig` z backendu (zawsze nowy kształt po E1.T3 migracji) i
 * zwraca `LayoutTab[]` zgodny ze stanem buildera.
 *
 * Fallback: jeśli backend zwróci jeszcze stary lub pre-C.7 kształt, też się
 * obsługa (defensive — np. dev'owy backend bez migracji).
 */
export function loadTabsFromConfig(layoutConfig: unknown): LayoutTab[] {
  if (isNewShape(layoutConfig)) {
    const tabsEl = layoutConfig.elements.find((el) => el.type === 'tabs');
    if (!tabsEl || !Array.isArray(tabsEl.tabs)) return [];
    return tabsEl.tabs.flatMap((item) => {
      const cleaned = dropRetiredExtFieldsBlock(item);
      return cleaned ? [convertNewTabItemToLayoutTab(cleaned)] : [];
    });
  }

  if (isOldShape(layoutConfig)) {
    // Zakładka „Pola dodatkowe" (`type: 'ext_fields'`) nie istnieje już w builderze — pomijamy ją,
    // zamiast konwertować. Dane z bazy mogą jeszcze nieść ten typ, stąd porównanie po `string`.
    return layoutConfig.tabs.filter((tab) => (tab.type as string) !== 'ext_fields');
  }

  if (isPreC7Shape(layoutConfig)) {
    return [
      {
        id: crypto.randomUUID(),
        slug: 'data',
        title: 'Dane',
        type: 'fields',
        hidden: false,
        sortOrder: 0,
        sections: layoutConfig.sections,
      },
    ];
  }

  return [];
}

/**
 * Czy element to wycofany blok „Pola dodatkowe" — generyczny `ext_fields_view` albo nazwany
 * `<moduł>_ext_fields_view` (np. `product_ext_fields_view`). Żaden nie ma już renderera ani
 * wpisu w palecie kreatora.
 */
function isRetiredExtFieldsBlock(el: LayoutElement): boolean {
  return el.type === 'ext_fields_view' || el.type.endsWith('_ext_fields_view');
}

/**
 * Usuwa wycofane bloki „Pola dodatkowe" z listy elementów oraz — rekurencyjnie — z `children`
 * zagnieżdżonych sekcji (blok bywa wstawiony w sekcję zakładki `data`). Sekcja opróżniona przez
 * to usunięcie ZOSTAJE (pusta sekcja to decyzja układu, nie zgniły blok). Bez zmian zwraca tę
 * samą tablicę (i te same obiekty).
 */
function stripRetiredExtFieldsBlocks(elements: LayoutElement[]): LayoutElement[] {
  let changed = false;
  const result: LayoutElement[] = [];
  for (const el of elements) {
    if (isRetiredExtFieldsBlock(el)) {
      changed = true;
      continue;
    }
    if (el.type === 'section' && Array.isArray(el.children)) {
      const cleanedChildren = stripRetiredExtFieldsBlocks(el.children);
      if (cleanedChildren !== el.children) {
        changed = true;
        result.push({ ...el, children: cleanedChildren });
        continue;
      }
    }
    result.push(el);
  }
  return changed ? result : elements;
}

/**
 * Usuwa z zakładki wycofany blok „Pola dodatkowe" — z dzieci pierwszego poziomu (tak stoi blok
 * w zakładce i wśród „extras") oraz z wnętrza zagnieżdżonych sekcji (`stripRetiredExtFieldsBlocks`).
 * Zakładka opróżniona PRZEZ usunięcie bloku z pierwszego poziomu (trzymała wyłącznie taki blok —
 * typowa zakładka „Pola dodatkowe") znika (`null`); zakładka pusta od początku, zakładka `data`
 * i zakładka z opróżnioną sekcją zostają — te same reguły co backfill po stronie API. Bez bloku
 * zwraca ten sam obiekt.
 */
function dropRetiredExtFieldsBlock(item: NewTabItem): NewTabItem | null {
  const children = item.children ?? [];
  const kept = stripRetiredExtFieldsBlocks(children);
  if (kept === children) return item;
  const removedFromTopLevel = kept.length < children.length;
  if (removedFromTopLevel && kept.length === 0 && item.slug !== 'data') return null;
  return { ...item, children: kept };
}

/**
 * Konwersja TabItem (nowy kształt) → LayoutTab (stary kształt w state buildera).
 *
 * Detektuje typ zakładki po zawartości `children`:
 *  - `[section, section, ...]` → type='fields' + sections z field-elementami
 *  - `[{type:'related_records_table', settings:{collection}}]` → type='subcollection'
 *  - `[{type:'audit_log_view'}]` → type='audit_log'
 *  - `[{type:'tree_picker_view', settings:{collection}}]` → type='tree_picker'
 *  - `[{type:'additional_view', settings:{tableSlug}}]` → type='additional' + tableSlug
 *    (+ `tableColumns` — układ kolumn zakładki, + `hiddenColumnIds` — maska ukrytych kolumn,
 *    na układzie systemowym nakładka tenanta). Z `settings` pierwszego dziecka zachowujemy
 *    WYŁĄCZNIE `tableSlug`, `tableColumns` i `hiddenColumnIds`; pozostałe klucze są przy
 *    wczytaniu i zapisie świadomie pomijane (zakładka tabeli dodatkowej nie ma innych ustawień —
 *    inaczej niż klocek `additional_view` w sekcji, który trzyma całe `settings`).
 *  - `[{type:'additional_query_view', settings:{viewSlug}}]` → type='additional_query' + viewSlug (F22;
 *    + `tableColumns` / `hiddenColumnIds` jak dla `additional_view` — z `settings` pierwszego dziecka
 *    zachowujemy WYŁĄCZNIE `viewSlug`, `tableColumns` i `hiddenColumnIds`)
 *  - `[{type:'panel_view'}]` → type='panel'
 *  - `[{type:'satellite_view', settings:{tableSlug}}]` → type='additional' (legacy, has tableSlug)
 *  - `[{type:'satellite_view'}]` → type='panel' (legacy, no tableSlug)
 *  - pusta lub nieznana → type='fields' z pustymi sections (safe default)
 */
function convertNewTabItemToLayoutTab(item: NewTabItem): LayoutTab {
  const base = {
    id: item.id,
    slug: item.slug,
    title: item.title,
    hidden: item.hidden ?? false,
    sortOrder: item.sortOrder ?? 0,
    ...(item.icon ? { icon: item.icon } : {}),
    ...(item.visibleRoleIds ? { visibleRoleIds: item.visibleRoleIds } : {}),
    ...(item.publishedAt ? { publishedAt: item.publishedAt } : {}),
  };

  const children = item.children ?? [];

  // Sprawdź pierwszy element — jeśli to placeholder typu non-fields, pozostałe
  // elementy są extras (klocki Treści/Komunikaty/Kontenery dodane przez admina).
  if (children.length > 0) {
    const first = children[0]!;
    const settings = (first.settings ?? {}) as { collection?: string };
    const extras = children.slice(1);

    if (first.type === 'related_records_table') {
      // F12 PR 2 pkt 2 v3 (2026-05-19): wydziel element `subcollection_form_layout`
      // z `extras` do `subcollectionFormLayout` (LayoutSection[]).
      let subcollectionFormLayout: LayoutSection[] | undefined;
      const realExtras: LayoutElement[] = [];
      for (const el of extras) {
        if (el.type === 'subcollection_form_layout' && Array.isArray(el.children)) {
          subcollectionFormLayout = el.children
            .filter((c) => c.type === 'section')
            .map((c) => {
              const s = (c.settings ?? {}) as { title?: string | null; columns?: number };
              return {
                id: c.id,
                title: s.title ?? null,
                columns: (s.columns ?? 2) as 1 | 2 | 3,
                elements: c.children ?? [],
              };
            });
        } else {
          realExtras.push(el);
        }
      }
      return {
        ...base,
        type: 'subcollection',
        ...(settings.collection ? { collection: settings.collection } : {}),
        ...(realExtras.length > 0 ? { extras: realExtras } : {}),
        ...(subcollectionFormLayout ? { subcollectionFormLayout } : {}),
      };
    }
    if (first.type === 'audit_log_view') {
      return { ...base, type: 'audit_log', ...(extras.length > 0 ? { extras } : {}) };
    }
    if (first.type === 'tree_picker_view') {
      return {
        ...base,
        type: 'tree_picker',
        ...(settings.collection ? { collection: settings.collection } : {}),
        ...(extras.length > 0 ? { extras } : {}),
      };
    }
    if (first.type === 'additional_view') {
      const tableSlug =
        typeof (settings as { tableSlug?: unknown }).tableSlug === 'string'
          ? (settings as { tableSlug: string }).tableSlug
          : undefined;
      const tableColumns = parseEmbeddedTableColumns(first.settings?.tableColumns);
      const hiddenColumnIds = parseEmbeddedTableHiddenColumnIds(first.settings?.hiddenColumnIds);
      return {
        ...base,
        type: 'additional',
        ...(tableSlug ? { tableSlug } : {}),
        ...(tableColumns ? { tableColumns } : {}),
        ...(hiddenColumnIds ? { hiddenColumnIds } : {}),
        ...(extras.length > 0 ? { extras } : {}),
      };
    }
    if (first.type === 'additional_query_view') {
      const viewSlug =
        typeof (settings as { viewSlug?: unknown }).viewSlug === 'string'
          ? (settings as { viewSlug: string }).viewSlug
          : undefined;
      const tableColumns = parseEmbeddedTableColumns(first.settings?.tableColumns);
      const hiddenColumnIds = parseEmbeddedTableHiddenColumnIds(first.settings?.hiddenColumnIds);
      return {
        ...base,
        type: 'additional_query',
        ...(viewSlug ? { viewSlug } : {}),
        ...(tableColumns ? { tableColumns } : {}),
        ...(hiddenColumnIds ? { hiddenColumnIds } : {}),
        ...(extras.length > 0 ? { extras } : {}),
      };
    }
    if (first.type === 'panel_view') {
      return { ...base, type: 'panel', ...(extras.length > 0 ? { extras } : {}) };
    }
    if (first.type === 'satellite_view') {
      // Legacy backward-compat: disambiguate by presence of tableSlug.
      const tableSlug =
        typeof (settings as { tableSlug?: unknown }).tableSlug === 'string'
          ? (settings as { tableSlug: string }).tableSlug
          : undefined;
      if (tableSlug) {
        const tableColumns = parseEmbeddedTableColumns(first.settings?.tableColumns);
        const hiddenColumnIds = parseEmbeddedTableHiddenColumnIds(first.settings?.hiddenColumnIds);
        return {
          ...base,
          type: 'additional',
          tableSlug,
          ...(tableColumns ? { tableColumns } : {}),
          ...(hiddenColumnIds ? { hiddenColumnIds } : {}),
          ...(extras.length > 0 ? { extras } : {}),
        };
      }
      return {
        ...base,
        type: 'panel',
        ...(extras.length > 0 ? { extras } : {}),
      };
    }
    // Etap 2 kartoteki: zakładka Akceptacje — seed zostawia puste children.
    // Detekcja po slug/tytule lub pustej zawartości gdy slug='approvals'.
    if (first.type === 'approvals_view') {
      return { ...base, type: 'approvals', ...(extras.length > 0 ? { extras } : {}) };
    }
  }

  // Pusta zakładka z slug 'approvals' — oznacza zakładkę Akceptacje (seed F17 T17.1.8)
  if (item.slug === 'approvals' && children.length === 0) {
    return { ...base, type: 'approvals' };
  }

  // type='fields' — sections + extras (klocki po sekcjach)
  const sections: LayoutSection[] = [];
  const extras: LayoutElement[] = [];
  for (const el of children) {
    if (el.type === 'section') {
      sections.push({
        id: el.id,
        title: (el.settings as { title?: string | null })?.title ?? null,
        columns: ((el.settings as { columns?: number })?.columns ?? 2) as 1 | 2 | 3,
        elements: el.children ?? [],
      });
    } else {
      extras.push(el);
    }
  }

  // Zakładka bez sekcji, złożona wyłącznie z bloków (np. „Kontakty" = jeden
  // `contractor_contacts_view`, „Załączniki" = `attachments_view`, „Akceptacje" =
  // `process_view`) → typ 'block'. Builder renderuje ją przez ścieżkę non-fields
  // (paleta + NonFieldsExtrasPanel), więc bloki są widoczne/usuwalne/dokładalne,
  // zamiast ginąć w niewidocznych `extras` pustej zakładki 'fields'.
  if (sections.length === 0 && extras.length > 0) {
    return { ...base, type: 'block', extras };
  }

  return {
    ...base,
    type: 'fields',
    sections,
    ...(extras.length > 0 ? { extras } : {}),
  };
}

// ─── SAVE: stary → nowy ──

/**
 * Konwertuje aktualny stan buildera (`LayoutTab[]`) do nowego rekurencyjnego
 * kształtu wymaganego przez walidator backendu (E1.T7).
 *
 * Wynik: `{ elements: [...viewLevelElements, { id, type: 'tabs', tabs: [...] }] }` —
 * elementy VIEW-LEVEL (page-header/highlight, T2) jako rodzeństwo PRZED elementem
 * `tabs`, potem jeden element type='tabs' ze wszystkimi TabItem-ami. Domyślnie
 * `viewLevelElements=[]` (zachowanie wsteczne = `[tabsElement]`).
 *
 * WAŻNE (T2): każda mutacja zakładek w buildery MUSI podać `viewLevelElements`
 * z aktualnego stanu, inaczej rodzeństwo view-level ginie na round-tripie.
 */
// Overloady: wywołanie 1-arg (bez view-level) zachowuje precyzyjny typ
// `{ elements: NewTabsElement[] }` — stare callsite'y/testy sięgają `elements[0].tabs`
// bez narrowowania. Wywołanie 2-arg zwraca unię (rodzeństwo view-level + tabs).
export function saveTabsToConfig(tabs: LayoutTab[]): { elements: NewTabsElement[] };
export function saveTabsToConfig(
  tabs: LayoutTab[],
  viewLevelElements: LayoutElement[],
): NewLayoutConfig;
export function saveTabsToConfig(
  tabs: LayoutTab[],
  viewLevelElements: LayoutElement[] = [],
): NewLayoutConfig {
  return {
    elements: [
      ...viewLevelElements,
      {
        id: crypto.randomUUID(),
        type: 'tabs',
        tabs: tabs.map(convertLayoutTabToNewTabItem),
      },
    ],
  };
}

/**
 * Wyciąga elementy VIEW-LEVEL (rodzeństwo elementu `tabs`) z surowego `layoutConfig`
 * nowego kształtu — wszystko co NIE jest elementem `type==='tabs'`. Dla starych
 * kształtów (`tabs[]` / `sections[]` / pusty) zwraca `[]` (brak rodzeństwa — zgodność
 * wsteczna, T2 zero migracji). Używane przy hydratacji stanu buildera oraz w
 * `updateTabs`, żeby round-trip zachował page-header/highlight.
 */
export function extractViewLevelElements(layoutConfig: unknown): LayoutElement[] {
  if (!isNewShape(layoutConfig)) return [];
  return (layoutConfig.elements as Array<{ type?: string }>).filter(
    (el) => el.type !== 'tabs',
  ) as LayoutElement[];
}

/**
 * Konwersja LayoutTab (stary kształt w state) → NewTabItem (nowy kształt do PUT).
 */
function convertLayoutTabToNewTabItem(tab: LayoutTab): NewTabItem {
  return {
    id: tab.id,
    slug: tab.slug ?? tab.type,
    title: tab.title,
    ...(tab.icon ? { icon: tab.icon } : {}),
    hidden: tab.hidden,
    sortOrder: tab.sortOrder,
    ...(tab.visibleRoleIds ? { visibleRoleIds: tab.visibleRoleIds } : {}),
    ...(tab.publishedAt ? { publishedAt: tab.publishedAt } : {}),
    children: buildChildrenFromLayoutTab(tab),
  };
}

/**
 * Buduje `children: LayoutElement[]` na podstawie typu i zawartości starego LayoutTab.
 *
 * Mapowanie:
 *  - 'fields' → sections.map(s → element typu 'section' z polami w children)
 *  - 'subcollection' → [{type:'related_records_table', settings:{collection}}]
 *  - 'audit_log' → [{type:'audit_log_view', settings:{}}]
 *  - 'tree_picker' → [{type:'tree_picker_view', settings:{collection}}]
 *  - 'additional' → [{type:'additional_view', settings:{tableSlug?, tableColumns?, hiddenColumnIds?}}]
 *    (nowe — tabela dodatkowa; to JEDYNE klucze `settings` zapisywane dla całej zakładki, patrz też
 *    `convertNewTabItemToLayoutTab`)
 *  - 'additional_query' → [{type:'additional_query_view', settings:{viewSlug?, tableColumns?, hiddenColumnIds?}}]
 *    (F22 — widok dodatkowy SQL; układ kolumn i maska jak w 'additional')
 *  - 'panel' → [{type:'panel_view', settings:{}}]  (nowe — placeholder bez tableSlug)
 *  - 'satellite' → [{type:'satellite_view', settings:{}}]  (legacy backward-compat)
 */
function buildChildrenFromLayoutTab(tab: LayoutTab): LayoutElement[] {
  const extras = tab.extras ?? [];

  switch (tab.type) {
    case 'fields': {
      const sections = tab.sections ?? [];
      const sectionElements: LayoutElement[] = sections.map((sec) => ({
        id: sec.id,
        type: 'section',
        settings: {
          title: sec.title,
          columns: sec.columns,
        },
        children: sec.elements,
      }));
      return [...sectionElements, ...extras];
    }

    case 'subcollection': {
      const rrtElement: LayoutElement = {
        id: crypto.randomUUID(),
        type: 'related_records_table',
        settings: tab.collection ? { collection: tab.collection } : {},
      };
      // F12 PR 2 pkt 2 v3: customowy form layout (jeśli admin go skonfigurował lub
      // domyślny seed został wykonany) idzie jako osobny element po `related_records_table`.
      if (tab.subcollectionFormLayout && tab.subcollectionFormLayout.length > 0) {
        const formLayoutElement: LayoutElement = {
          id: crypto.randomUUID(),
          type: 'subcollection_form_layout',
          settings: {},
          children: tab.subcollectionFormLayout.map((sec) => ({
            id: sec.id,
            type: 'section',
            settings: { title: sec.title, columns: sec.columns },
            children: sec.elements,
          })),
        };
        return [rrtElement, formLayoutElement, ...extras];
      }
      return [rrtElement, ...extras];
    }

    case 'audit_log':
      return [
        {
          id: crypto.randomUUID(),
          type: 'audit_log_view',
          settings: {},
        },
        ...extras,
      ];

    case 'tree_picker':
      return [
        {
          id: crypto.randomUUID(),
          type: 'tree_picker_view',
          settings: tab.collection ? { collection: tab.collection } : {},
        },
        ...extras,
      ];

    case 'additional':
      return [
        {
          id: crypto.randomUUID(),
          type: 'additional_view' as const,
          settings: {
            ...(tab.tableSlug ? { tableSlug: tab.tableSlug } : {}),
            // Klucze tylko przy niepustej wartości — domyślny układ / brak maski = brak klucza w JSONB.
            ...(tab.tableColumns?.length ? { tableColumns: tab.tableColumns } : {}),
            ...(tab.hiddenColumnIds?.length ? { hiddenColumnIds: tab.hiddenColumnIds } : {}),
          },
        },
        ...extras,
      ];

    case 'additional_query':
      return [
        {
          id: crypto.randomUUID(),
          type: 'additional_query_view' as const,
          settings: {
            ...(tab.viewSlug ? { viewSlug: tab.viewSlug } : {}),
            // Jak w `additional`: klucze tylko przy niepustej wartości.
            ...(tab.tableColumns?.length ? { tableColumns: tab.tableColumns } : {}),
            ...(tab.hiddenColumnIds?.length ? { hiddenColumnIds: tab.hiddenColumnIds } : {}),
          },
        },
        ...extras,
      ];

    case 'panel':
      return [
        {
          id: crypto.randomUUID(),
          type: 'panel_view' as const,
          settings: {},
        },
        ...extras,
      ];

    // Legacy backward-compat: un-migrated stored JSONB with satellite type.
    case 'satellite':
      return [
        {
          id: crypto.randomUUID(),
          type: 'satellite_view',
          settings: {
            ...(tab.tableSlug ? { tableSlug: tab.tableSlug } : {}),
            ...(tab.tableColumns?.length ? { tableColumns: tab.tableColumns } : {}),
            ...(tab.hiddenColumnIds?.length ? { hiddenColumnIds: tab.hiddenColumnIds } : {}),
          },
        },
        ...extras,
      ];

    // Etap 2 kartoteki: zakładka Akceptacje — seed tworzy pustą tablicę children.
    // Treść renderuje runtime po stronie tenanta (ProcessHistoryTab).
    case 'approvals':
      return [...extras];

    // Zakładka-kontener bloków (Faza 1): children = bloki (extras) 1:1, w kolejności.
    // Tu żyją nazwane bloki kartoteki (contractor_*_view / attachments_view /
    // process_view / record_links_view …). Round-trip idealny — load robi z nich
    // typ 'block', save zwraca je z powrotem bez owijania.
    case 'block':
      return [...extras];

    default:
      return [];
  }
}
